/* ==========================================================================
   LAST PALLET - idle auto-advance

   Both the hero and the material flight are scroll-scrubbed: nothing moves
   until the visitor scrolls, and the only cue that they should is a small
   "Scroll" hint in the hero. Easy to miss on a first visit. If nobody
   touches a scroll input a few seconds after load, the page advances
   itself, so motion is what teaches a first-time visitor that the page
   responds to scrolling.

   It runs in two stages, because the two sections want opposite things:

   1. The hero hands off to native video playback (hero-scrub.js exposes
      `heroIntro`). Scroll-scrubbing a video is inherently steppy - every
      update asks the decoder for a random-access frame and it lands on
      whatever it can decode in time - so stepping the scrubber on a timer
      looked visibly clanky. During the intro the video plays normally and
      *scroll* is derived from currentTime instead, which is smooth because
      it is ordinary playback.

   2. Past the hero, the material flight is just CSS background-position
      with no decoding involved, so a plain per-frame rAF drift is smooth
      and needs no throttling.

   The instant a person provides real input - wheel, touch, key, click -
   control is handed back permanently. This only ever fires once, before
   anyone has touched anything; it never resumes after a person takes over,
   even if they later sit still to read something.

   Vanilla, no dependencies, no build step.
   ========================================================================== */

(function () {
  'use strict';

  /* A section can legitimately appear more than once on a page, and each
     copy emits its own <script> tag. Bind only once so listeners and
     rAF loops are never doubled up. */
  if (window.__lpAutoAdvance) { return; }
  window.__lpAutoAdvance = true;

  var IDLE_MS = 2600;   // wait this long with zero input before starting
  var SPEED = 150;      // px per second for the post-hero drift

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCompact() {
    return window.matchMedia('(max-width: 780px)').matches;
  }

  // Mobile already scrolls normally with no pinned scrub to explain, and
  // reduced motion should never move the page on its own.
  if (prefersReducedMotion() || isCompact()) { return; }

  var idleTimer = null;
  var rafId = null;
  var lastFrame = null;
  var handedOver = false;

  var CANCEL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

  function hero() {
    var el = document.querySelector('[data-hero-scrub]');
    return el && el.heroIntro ? el.heroIntro : null;
  }

  function stopDrift() {
    if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
    lastFrame = null;
  }

  function handOver() {
    if (handedOver) { return; }
    handedOver = true;
    if (idleTimer) { window.clearTimeout(idleTimer); idleTimer = null; }
    stopDrift();
    var h = hero();
    if (h && h.isPlaying()) { h.stop(); }
    CANCEL_EVENTS.forEach(function (evt) {
      window.removeEventListener(evt, handOver);
    });
  }

  function driftFrame(ts) {
    if (handedOver) { return; }
    if (lastFrame == null) { lastFrame = ts; }
    var dt = Math.min((ts - lastFrame) / 1000, 0.05);  // clamp after a tab stall
    lastFrame = ts;

    var atBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
    if (atBottom) { stopDrift(); return; }

    // The two-argument scrollBy(x, y) form respects the page's CSS
    // scroll-behavior: smooth (set globally for anchor-link jumps), which
    // fights a per-frame call - each increment restarts a smooth animation
    // the next frame interrupts, netting a fraction of the intended speed.
    // The options form forces instant motion regardless.
    window.scrollBy({ top: SPEED * dt, left: 0, behavior: 'instant' });
    rafId = window.requestAnimationFrame(driftFrame);
  }

  function startDrift() {
    if (handedOver || rafId) { return; }
    lastFrame = null;
    rafId = window.requestAnimationFrame(driftFrame);
  }

  function begin() {
    if (handedOver) { return; }
    var h = hero();
    // Play the hero natively first; fall straight through to the drift if
    // the hero isn't in a state to play (already scrolled, still loading,
    // autoplay blocked).
    if (h && h.play(startDrift)) { return; }
    startDrift();
  }

  CANCEL_EVENTS.forEach(function (evt) {
    window.addEventListener(evt, handOver, { passive: true });
  });

  idleTimer = window.setTimeout(function () {
    idleTimer = null;
    begin();
  }, IDLE_MS);
})();
